import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import SelectFarmWarehouse from "../../../components/SelectFarmWarehouse";
import { getAPIDataParamsReturn } from "../../../utils/apiFunction";
import { numberFormatErrorToZero as numberFormat } from "../../../utils/numberFunctions";
import { checkAccess } from "../../../utils/roleFunctions";
import { ACCESS, ACCESS_SECTIONS } from "../../../utils/constants/access";
import { buildOysterMateTotalsData, mergeOysterMateGroups } from "../../Farms/oysterMateTotalsUtils";

const sumPeriodTotals = (periods) => {
    if (!Array.isArray(periods)) {
        return 0;
    }

    return periods.reduce((sum, period) => sum + (Number(period?.totalOysters) || 0), 0);
};

const getLatestCurrentStock = (report) => {
    const gradePeriods = Array.isArray(report?.gradePeriods) ? report.gradePeriods : [];
    if (gradePeriods.length === 0) {
        return 0;
    }

    return Number(gradePeriods[gradePeriods.length - 1]?.totalOysters) || 0;
};

const CROP_YEAR_MODE = {
    NONE: "none",
    EACH: "each",
};

const toBatchKey = (batch, includeCropYear = false) => {
    const cropYearPart = includeCropYear ? (batch?.cropYear || "") : "";

    return [
        batch?.cropId || "",
        batch?.supplierKey || "",
        batch?.stockType || "",
        cropYearPart,
        batch?.batch || "",
    ].join("|");
};

const dedupeBatches = (batches, includeCropYear = false) => {
    const seen = new Set();
    const unique = [];

    (Array.isArray(batches) ? batches : []).forEach((batch) => {
        const key = toBatchKey(batch, includeCropYear);
        if (seen.has(key)) {
            return;
        }

        seen.add(key);
        unique.push(batch);
    });

    return unique;
};

const normalizeBatchLabel = (batchLabel) => {
    if (typeof batchLabel !== "string") {
        return "";
    }

    return batchLabel.trim().replace(/\s+/g, " ").toLowerCase();
};

const mapWithConcurrency = async (items, concurrency, mapper) => {
    const results = new Array(items.length);
    let nextIndex = 0;

    const workers = Array.from({ length: Math.max(1, Math.min(concurrency, items.length || 1)) }, async () => {
        while (true) {
            const currentIndex = nextIndex;
            nextIndex += 1;

            if (currentIndex >= items.length) {
                return;
            }

            results[currentIndex] = await mapper(items[currentIndex], currentIndex);
        }
    });

    await Promise.all(workers);
    return results;
};

const BatchReportAnalysis = ({ access }) => {
    const [farm, setFarm] = React.useState(null);
    const [cropYearMode, setCropYearMode] = React.useState(CROP_YEAR_MODE.NONE);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [analysisRows, setAnalysisRows] = React.useState([]);

    const hasMasterAccess = checkAccess(access, { key: ACCESS_SECTIONS.FARMS, value: ACCESS.MASTER });

    const summary = React.useMemo(() => {
        const total = analysisRows.length;
        const netFlowMatches = analysisRows.filter((row) => row.matchesNetFlow).length;
        const batchesMatches = analysisRows.filter((row) => row.matchesBatchesValue).length;

        return {
            total,
            netFlowMatches,
            netFlowMismatches: total - netFlowMatches,
            batchesMatches,
            batchesMismatches: total - batchesMatches,
        };
    }, [analysisRows]);

    const runAnalysis = async () => {
        if (!farm?.warehouseId) {
            return;
        }

        setLoading(true);
        setError("");
        setAnalysisRows([]);

        try {
            const [filtersData, waterGroups, onShoreGroups] = await Promise.all([
                getAPIDataParamsReturn("/Farms/BatchReporting/BatchReportFilters", {
                    WarehouseId: farm.warehouseId,
                }),
                getAPIDataParamsReturn("/Farms/Groups", {
                    WarehouseId: farm.warehouseId,
                }),
                getAPIDataParamsReturn("/Farms/Groups/OnShore", {
                    WarehouseId: farm.warehouseId,
                }),
            ]);

            const includeCropYear = cropYearMode === CROP_YEAR_MODE.EACH;
            const batches = dedupeBatches(filtersData?.batches || [], includeCropYear);
            if (batches.length === 0) {
                setAnalysisRows([]);
                return;
            }

            const mergedGroups = mergeOysterMateGroups(waterGroups, onShoreGroups);
            const batchTotals = buildOysterMateTotalsData(mergedGroups, "By Batch")
                .filter((row) => row.totalEnd > 0)
                .reduce((map, row) => {
                    const normalizedBatch = normalizeBatchLabel(row.batch);
                    if (!normalizedBatch) {
                        return map;
                    }

                    const currentTotal = map.get(normalizedBatch) || 0;
                    map.set(normalizedBatch, currentTotal + (Number(row.totalEnd) || 0));
                    return map;
                }, new Map());

            const rows = await mapWithConcurrency(batches, 4, async (batch) => {
                const report = await getAPIDataParamsReturn("/Farms/BatchReporting/BatchReport", {
                    WarehouseId: farm.warehouseId,
                    CropId: batch.cropId,
                    SupplierId: batch.supplierKey,
                    StockType: batch.stockType,
                    CropYear: includeCropYear ? batch.cropYear : undefined,
                    PeriodType: "month",
                });

                const totalIn =
                    sumPeriodTotals(report?.receivalPeriods)
                    + sumPeriodTotals(report?.backToWaterPeriods)
                    + Math.max(sumPeriodTotals(report?.variancePeriods), 0)
                    + Math.max(sumPeriodTotals(report?.offSlatsPeriods), 0);

                const totalOut =
                    sumPeriodTotals(report?.transferPeriods)
                    + sumPeriodTotals(report?.untrackedRemovalPeriods)
                    + sumPeriodTotals(report?.mixedPeriods)
                    + Math.max(-sumPeriodTotals(report?.variancePeriods), 0)
                    + Math.max(-sumPeriodTotals(report?.offSlatsPeriods), 0);

                const netFlow = totalIn - totalOut;
                const currentStock = getLatestCurrentStock(report);
                const netDiff = netFlow - currentStock;
                const batchesValue = batchTotals.get(normalizeBatchLabel(batch.batch)) || 0;
                const batchesDiff = currentStock - batchesValue;

                return {
                    key: toBatchKey(batch, includeCropYear),
                    batch: batch.batch || "",
                    cropName: batch.cropName || "",
                    cropYear: batch.cropYear || "",
                    stockType: batch.stockType || "",
                    cropDate: "All",
                    totalIn,
                    totalOut,
                    netFlow,
                    currentStock,
                    netDiff,
                    matchesNetFlow: netDiff === 0,
                    batchesValue,
                    batchesDiff,
                    matchesBatchesValue: batchesDiff === 0,
                };
            });

            rows.sort((left, right) => {
                if (left.matchesNetFlow !== right.matchesNetFlow) {
                    return left.matchesNetFlow ? 1 : -1;
                }

                if (left.matchesBatchesValue !== right.matchesBatchesValue) {
                    return left.matchesBatchesValue ? 1 : -1;
                }

                return (left.batch || "").localeCompare(right.batch || "");
            });

            setAnalysisRows(rows);
        } catch (analysisError) {
            console.error(analysisError);
            setError("Unable to run batch report analysis for this farm.");
        } finally {
            setLoading(false);
        }
    };

    if (!hasMasterAccess) {
        return (
            <Alert severity="warning">
                You are not authorized to view this page.
            </Alert>
        );
    }

    return (
        <Grid container spacing={2} mb={1}>
            <Grid item xs={12}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                    <Box sx={{ minWidth: 320 }}>
                        <SelectFarmWarehouse setState={setFarm} state={farm} access={access} />
                    </Box>
                    <ToggleButtonGroup
                        color="primary"
                        exclusive
                        value={cropYearMode}
                        onChange={(_, nextValue) => {
                            if (nextValue) {
                                setCropYearMode(nextValue);
                            }
                        }}
                        size="small"
                    >
                        <ToggleButton value={CROP_YEAR_MODE.NONE}>No Crop Year</ToggleButton>
                        <ToggleButton value={CROP_YEAR_MODE.EACH}>Each Crop Year</ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        variant="contained"
                        onClick={runAnalysis}
                        disabled={!farm?.warehouseId || loading}
                    >
                        {loading ? "Running..." : "Submit"}
                    </Button>
                </Stack>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Batch Report Analysis"
                        subheader="Runs Batch Report per filter combination and compares reconciliations. Crop Year mode defaults to sending no crop year."
                    />
                    <CardContent>
                        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

                        {loading ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Running analysis...</Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={2}>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                                    <Alert severity={summary.netFlowMismatches > 0 ? "warning" : "success"}>
                                        Net Flow Checks: {summary.netFlowMatches} / {summary.total} matched
                                    </Alert>
                                    <Alert severity={summary.batchesMismatches > 0 ? "warning" : "success"}>
                                        Batches Value Checks: {summary.batchesMatches} / {summary.total} matched
                                    </Alert>
                                </Stack>

                                {analysisRows.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No analysis rows to display. Select a farm and press Submit.
                                    </Typography>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Batch</TableCell>
                                                    <TableCell>Crop</TableCell>
                                                    <TableCell>Crop Year</TableCell>
                                                    <TableCell>Stock Type</TableCell>
                                                    <TableCell>Crop Date</TableCell>
                                                    <TableCell align="right">Total In</TableCell>
                                                    <TableCell align="right">Total Out</TableCell>
                                                    <TableCell align="right">Net Flow</TableCell>
                                                    <TableCell align="right">Current Stock</TableCell>
                                                    <TableCell align="right">Net - Current</TableCell>
                                                    <TableCell>Net Matches</TableCell>
                                                    <TableCell align="right">Batches Page Value</TableCell>
                                                    <TableCell align="right">Current - Batches</TableCell>
                                                    <TableCell>Batches Match</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {analysisRows.map((row) => (
                                                    <TableRow
                                                        key={row.key}
                                                        sx={{
                                                            backgroundColor: row.matchesNetFlow && row.matchesBatchesValue
                                                                ? "rgba(46, 125, 50, 0.14)"
                                                                : "rgba(211, 47, 47, 0.14)",
                                                        }}
                                                    >
                                                        <TableCell>{row.batch}</TableCell>
                                                        <TableCell>{row.cropName}</TableCell>
                                                        <TableCell>{row.cropYear}</TableCell>
                                                        <TableCell>{row.stockType}</TableCell>
                                                        <TableCell>{row.cropDate}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.totalIn)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.totalOut)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.netFlow)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.currentStock)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.netDiff)}</TableCell>
                                                        <TableCell>{row.matchesNetFlow ? "Yes" : "No"}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.batchesValue)}</TableCell>
                                                        <TableCell align="right">{numberFormat(row.batchesDiff)}</TableCell>
                                                        <TableCell>{row.matchesBatchesValue ? "Yes" : "No"}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default BatchReportAnalysis;
